import { useMemo } from "react";
import type { ReactNode } from "react";
import guide from "../../../USER_GUIDE.md?raw";
import { useSheetDismiss } from "../useSheetDismiss";
import { ScrollTopButton } from "./ScrollTopButton";

/**
 * The user guide, read from the same file that lives in the repository.
 *
 * One source, so the document and the thing in the rider's hand cannot drift apart. The
 * renderer below only understands the handful of constructs that file actually uses; anything
 * else would go unnoticed here and be obvious in the file, which is the right way round.
 */
export function GuideSheet({ onClose }: { onClose: () => void }) {
	const dismiss = useSheetDismiss(onClose);
	const blocks = useMemo(() => render(guide), []);

	return (
		<div className="sheet" role="dialog" aria-label="Using GoTime">
			<div className="sheet__scrim" onClick={onClose} />
			<div className="sheet__body" ref={dismiss.bodyRef} style={dismiss.style} {...dismiss.handlers}>
				<span className="sheet__grip" aria-hidden="true" />
				<ScrollTopButton targetRef={dismiss.bodyRef} className="sheet__toTop" />
				<header className="sheet__head">
					<h2>Using GoTime</h2>
					<button type="button" onClick={onClose} aria-label="Close">
						✕
					</button>
				</header>
				<article className="guide">{blocks}</article>
			</div>
		</div>
	);
}

/** `**bold**` and `` `code` ``, which is all the prose needs. */
function inline(text: string, key: string): ReactNode[] {
	return text.split(/(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g).map((part, index) => {
		if (part.startsWith("**") && part.endsWith("**")) {
			return <strong key={`${key}-${index}`}>{part.slice(2, -2)}</strong>;
		}
		if (part.startsWith("`") && part.endsWith("`")) {
			return <code key={`${key}-${index}`}>{part.slice(1, -1)}</code>;
		}
		if (part.length > 2 && part.startsWith("*") && part.endsWith("*")) {
			return <em key={`${key}-${index}`}>{part.slice(1, -1)}</em>;
		}
		return part;
	});
}

function render(source: string): ReactNode[] {
	const out: ReactNode[] = [];
	let list: string[] = [];
	let paragraph: string[] = [];

	const flushList = () => {
		if (list.length === 0) {
			return;
		}
		const items = list;
		list = [];
		out.push(
			<ul key={`ul-${out.length}`}>
				{items.map((item, index) => (
					<li key={index}>{inline(item, `li-${out.length}-${index}`)}</li>
				))}
			</ul>,
		);
	};

	const flushParagraph = () => {
		if (paragraph.length === 0) {
			return;
		}
		const text = paragraph.join(" ");
		paragraph = [];
		out.push(<p key={`p-${out.length}`}>{inline(text, `p-${out.length}`)}</p>);
	};

	const flush = () => {
		flushParagraph();
		flushList();
	};

	for (const raw of source.split("\n")) {
		const line = raw.trimEnd();

		if (line.trim() === "") {
			flush();
			continue;
		}
		if (line.startsWith("---")) {
			flush();
			out.push(<hr key={`hr-${out.length}`} />);
			continue;
		}
		if (line.startsWith("> ")) {
			flush();
			out.push(<blockquote key={`q-${out.length}`}>{inline(line.slice(2), `q-${out.length}`)}</blockquote>);
			continue;
		}

		const heading = /^(#{1,3})\s+(.*)$/.exec(line);
		if (heading) {
			flush();
			const level = heading[1].length;
			const text = inline(heading[2], `h-${out.length}`);
			// The sheet header already says "Using GoTime", so the document's own title is noise.
			if (level === 1) {
				continue;
			}
			out.push(level === 2 ? <h3 key={`h-${out.length}`}>{text}</h3> : <h4 key={`h-${out.length}`}>{text}</h4>);
			continue;
		}

		const image = /^!\[([^\]]*)\]\(([^)]+)\)$/.exec(line);
		if (image) {
			flush();
			// The file is written for someone reading the repository; served, `public` is the root.
			const src = image[2].replace(/^public\//, "/");
			out.push(<img key={`img-${out.length}`} className="guide__shot" src={src} alt={image[1]} />);
			continue;
		}

		if (line.startsWith("- ")) {
			flushParagraph();
			list.push(line.slice(2));
			continue;
		}

		flushList();
		paragraph.push(line.trim());
	}

	flush();
	return out;
}
